// src/hooks/useAuth.js
// Hook centralizado de sesión. Evita que cada componente acceda directamente
// a localStorage y unifica el login/logout en un solo lugar.
import { useNavigate } from 'react-router-dom';

// Duración de la sesión SAP (el token vence a los 30 min por defecto).
// Configurable vía REACT_APP_SESSION_MINUTES.
const SESSION_MINUTES = Number(process.env.REACT_APP_SESSION_MINUTES) || 30;
export const SESSION_DURATION_MS = SESSION_MINUTES * 60 * 1000;

// --- Helpers de expiración (utilizables fuera de React) ---
export const getSessionExpiry = () => {
  const raw = localStorage.getItem('sap_session_expira');
  const ts = raw ? Number(raw) : 0;
  return Number.isFinite(ts) ? ts : 0;
};

export const isSessionExpired = () => {
  const expira = getSessionExpiry();
  // Si no hay marca de expiración pero sí token, lo tratamos como válido
  // (sesión antigua); sólo vence si la marca existe y ya pasó.
  return expira > 0 && Date.now() >= expira;
};

export const clearSession = () => {
  localStorage.removeItem('sap_session_token');
  localStorage.removeItem('usuario');
  localStorage.removeItem('sap_session_expira');
};

// Roles del Portal de Compras. Vienen de SAP (tabla @PORTAL_USUARIOS) y llegan
// dentro del token, que los emite el backend en el login.
export const ROLES = {
  SUPER:     'SUPER',
  ADMIN:     'COMP_ADMIN',
  COMPRADOR: 'COMP_COMPRADOR',
  LECTOR:    'COMP_LECTOR'
};

export const useAuth = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem('sap_session_token');

  let usuario = {};
  try {
    usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  } catch {
    usuario = {};
  }

  const roles = Array.isArray(usuario.roles) ? usuario.roles : [];
  const tiene = (...permitidos) => roles.some(r => permitidos.includes(r));

  // Esconder un botón es comodidad, no seguridad: el permiso de verdad lo aplica
  // el backend en cada endpoint que escribe. Esto sólo evita mostrarle a alguien
  // acciones que le van a dar 403.
  const puedeOperar     = tiene(ROLES.SUPER, ROLES.ADMIN, ROLES.COMPRADOR);
  const puedeConfigurar = tiene(ROLES.SUPER, ROLES.ADMIN);

  const login = (sessionToken, userData) => {
    localStorage.setItem('sap_session_token', sessionToken);
    localStorage.setItem('usuario', JSON.stringify(userData || {}));
    // Registramos el instante en que vence el token.
    localStorage.setItem('sap_session_expira', String(Date.now() + SESSION_DURATION_MS));
  };

  const logout = () => {
    clearSession();
    navigate('/login');
  };

  return {
    token,
    usuario,
    roles,
    puedeOperar,
    puedeConfigurar,
    isAuthenticated: Boolean(token) && !isSessionExpired(),
    login,
    logout,
  };
};

export default useAuth;
